/**
 * App shell: navigation, identity and the synthetic-tenant banner.
 *
 * The banner is not decoration. A synthetic tenant must be visibly labelled in
 * every relevant view (PRD 7.3), so it sits above the navigation on every
 * screen rather than being left to individual pages to remember.
 */

import { Link, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useHealth, useIdentity } from './hooks/useSession'
import { getTenantOverride, setTenantOverride } from './lib/api'
import { SignIn } from './pages/SignIn'
import { PlannerHome } from './pages/PlannerHome'
import { BriefWorkspace } from './pages/BriefWorkspace'
import { Recommendations } from './pages/Recommendations'
import { PlanReview } from './pages/PlanReview'
import { PlanCompare } from './pages/PlanCompare'
import { DataHealth } from './pages/DataHealth'
import { ScoringConfigAdmin } from './pages/ScoringConfigAdmin'
import { UserAdmin } from './pages/UserAdmin'
import { TenantAdmin } from './pages/TenantAdmin'
import { Skeleton } from './components/Metric'

export function App() {
  const { me, loading, signIn, signOut } = useIdentity()
  const health = useHealth()
  const location = useLocation()
  // The brief and recommendation screens are stages of a plan, not a distinct
  // "Home" workflow, so the Plans nav item stays highlighted through all of
  // /plans, /brief/:id and /recommendations/:id - not just the /plans list.
  const inPlansWorkflow = ['/plans', '/brief', '/recommendations'].some(
    (prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`),
  )

  if (loading) {
    return (
      <div className="page" style={{ padding: 24, maxWidth: 420 }}>
        <Skeleton rows={4} />
      </div>
    )
  }

  if (!me) return <SignIn onSignIn={signIn} health={health} />

  return (
    <div className="app">
      <div>
        {me.tenant_is_synthetic ? (
          <div className="banner synthetic">
            <span aria-hidden>▲</span>
            Sample data tenant “{me.tenant_id}”. Inventory, prices, audience and mobility figures
            are generated. Nothing here can be quoted to a client or booked.
          </div>
        ) : null}
        <header className="topbar">
          <div className="brand">
            Captura <span>AI Planner</span>
          </div>
          <nav className="nav" aria-label="Main">
            <NavLink to="/" end>
              Home
            </NavLink>
            <Link to="/plans" aria-current={inPlansWorkflow ? 'page' : undefined}>
              Plans
            </Link>
            {me.permissions.includes('data:inspect') ? (
              <NavLink to="/data-health">Data health</NavLink>
            ) : null}
            {me.permissions.includes('admin:config') ? (
              <NavLink to="/admin/scoring">Scoring config</NavLink>
            ) : null}
            {me.permissions.includes('user:manage') ? (
              <NavLink to="/admin/users">Users</NavLink>
            ) : null}
            {me.permissions.includes('tenant:manage') ? (
              <NavLink to="/admin/tenants">Tenants</NavLink>
            ) : null}
          </nav>
          <div className="topbar-right">
            {me.permissions.includes('tenant:manage') && getTenantOverride() ? (
              <span
                className="chip warn"
                title="You switched into this tenant from the Tenants screen. Every screen shows its data until you reset."
              >
                Viewing {me.tenant_id}{' '}
                <button
                  className="small ghost"
                  style={{ padding: '0 4px' }}
                  onClick={() => {
                    setTenantOverride(null)
                    window.location.reload()
                  }}
                >
                  reset
                </button>
              </span>
            ) : null}
            {health && health.llm.enabled === false ? (
              <span
                className="chip warn"
                title="No LLM is configured. Briefs are parsed with the deterministic extractor and conversational planning is off."
              >
                No model
              </span>
            ) : null}
            {health && health.captura_db.configured === false ? (
              <span
                className="chip neutral"
                title="CAPTURA_DATABASE_URL is not set, so planning runs against the sample tenant."
              >
                Captura DB off
              </span>
            ) : null}
            <span className="small muted nowrap">
              {me.display_name ?? me.email}
              <span className="faint"> · {me.role.replace(/_/g, ' ')}</span>
            </span>
            <button className="small ghost" onClick={signOut}>
              Sign out
            </button>
          </div>
        </header>
      </div>

      <Routes>
        <Route path="/" element={<PlannerHome />} />
        <Route path="/brief" element={<Navigate to="/" replace />} />
        <Route path="/brief/:briefId" element={<BriefWorkspace />} />
        <Route path="/recommendations" element={<Navigate to="/" replace />} />
        <Route path="/recommendations/:briefId" element={<Recommendations />} />
        <Route path="/plans" element={<PlanReview />} />
        <Route path="/plans/new" element={<PlannerHome />} />
        <Route path="/plans/:planId" element={<PlanReview />} />
        <Route path="/plans/:planId/compare" element={<PlanCompare />} />
        <Route path="/data-health" element={<DataHealth />} />
        <Route path="/admin/scoring" element={<ScoringConfigAdmin />} />
        <Route path="/admin/users" element={<UserAdmin />} />
        <Route path="/admin/tenants" element={<TenantAdmin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  )
}
